import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { getAllFlightsForUsers } from '../../services/supabaseService';
import { getCoords, getCountry, greatCircleDistance } from '../../utils/airportCoords';
import { Loader2, Trophy } from 'lucide-react';
import { haptic } from '../../utils/haptics';
import type { User, Flight } from '../../types';

// ── Types ──────────────────────────────────────────────

interface LeaderboardEntry {
  user: User;
  nauticalMiles: number;
  flightCount: number;
  uniqueAirports: number;
  uniqueCountries: number;
}

type SortCategory = 'miles' | 'flights' | 'airports' | 'countries';

const CATEGORIES: { key: SortCategory; label: string }[] = [
  { key: 'miles', label: 'Nautical Miles' },
  { key: 'flights', label: 'Flights' },
  { key: 'airports', label: 'Airports' },
  { key: 'countries', label: 'Countries' },
];

// ── Helpers ────────────────────────────────────────────

function computeStats(flights: Flight[]): Omit<LeaderboardEntry, 'user'> {
  let nauticalMiles = 0;
  const airports = new Set<string>();
  const countries = new Set<string>();

  for (const f of flights) {
    const from = getCoords(f.departureAirport);
    const to = getCoords(f.arrivalAirport);
    if (from && to) nauticalMiles += greatCircleDistance(from, to);

    airports.add(f.departureAirport);
    airports.add(f.arrivalAirport);

    const c1 = getCountry(f.departureAirport);
    const c2 = getCountry(f.arrivalAirport);
    if (c1) countries.add(c1);
    if (c2) countries.add(c2);
  }

  return {
    nauticalMiles: Math.round(nauticalMiles),
    flightCount: flights.length,
    uniqueAirports: airports.size,
    uniqueCountries: countries.size,
  };
}

function sortValue(e: LeaderboardEntry, cat: SortCategory): number {
  switch (cat) {
    case 'miles': return e.nauticalMiles;
    case 'flights': return e.flightCount;
    case 'airports': return e.uniqueAirports;
    case 'countries': return e.uniqueCountries;
  }
}

function formatValue(v: number, cat: SortCategory): string {
  if (cat === 'miles') {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 10_000) return `${(v / 1_000).toFixed(0)}k`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return v.toLocaleString();
  }
  return v.toLocaleString();
}

function unitLabel(cat: SortCategory): string {
  switch (cat) {
    case 'miles': return 'nm';
    case 'flights': return 'flights';
    case 'airports': return 'airports';
    case 'countries': return 'countries';
  }
}

function initial(user: User): string {
  return (user.name || user.handle).charAt(0).toUpperCase();
}

// ── Avatar component ───────────────────────────────────

function Avatar({ user, size }: { user: User; size: 'sm' | 'md' | 'lg' }) {
  const dims = size === 'lg' ? 'w-16 h-16' : size === 'md' ? 'w-12 h-12' : 'w-9 h-9';
  const textSize = size === 'lg' ? 'text-xl' : size === 'md' ? 'text-base' : 'text-xs';

  return (
    <div className={`${dims} rounded-full bg-slate-700/60 flex items-center justify-center shrink-0 overflow-hidden`}>
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className={`${textSize} font-bold text-slate-400`}>{initial(user)}</span>
      )}
    </div>
  );
}

// ── Podium (top 3) ─────────────────────────────────────

function Podium({
  entries,
  category,
  currentUserId,
}: {
  entries: LeaderboardEntry[];
  category: SortCategory;
  currentUserId: string;
}) {
  if (entries.length < 2) return null;

  // Layout order: 2nd - 1st - 3rd
  const slots = [entries[1], entries[0], entries.length > 2 ? entries[2] : null];
  const medals = ['\u{1F948}', '\u{1F947}', '\u{1F949}'];
  const ringColors = [
    'ring-2 ring-slate-400/60', // silver
    'ring-3 ring-amber-500/70', // gold
    'ring-2 ring-amber-700/50', // bronze
  ];
  const blockHeights = ['h-11', 'h-16', 'h-8'];
  const blockBgs = [
    'bg-slate-600/20 border-slate-600/30',
    'bg-amber-500/10 border-amber-500/20',
    'bg-amber-800/10 border-amber-800/20',
  ];

  return (
    <div className="flex items-end justify-center gap-2.5 mb-5 px-2">
      {slots.map((entry, i) => {
        if (!entry) return <div key={i} className="flex-1 max-w-[110px]" />;
        const isMe = entry.user.id === currentUserId;
        const val = sortValue(entry, category);
        const rank = i === 1 ? 1 : i === 0 ? 2 : 3;

        return (
          <div key={entry.user.id} className="flex flex-col items-center flex-1 max-w-[110px]">
            {/* Crown for #1 */}
            {rank === 1 && <span className="text-lg mb-0.5">{'\u{1F451}'}</span>}

            {/* Avatar with ring */}
            <div className="relative mb-1.5">
              <div className={`rounded-full ${ringColors[i]} ${i === 1 ? 'p-0.5' : 'p-px'}`}>
                <Avatar user={entry.user} size={i === 1 ? 'lg' : 'md'} />
              </div>
              <span className="absolute -bottom-1 -right-0.5 text-sm bg-slate-800 rounded-full px-0.5">
                {medals[i]}
              </span>
            </div>

            {/* Name */}
            <span className={`text-xs font-semibold truncate max-w-full ${isMe ? 'text-accent' : 'text-slate-200'}`}>
              {isMe ? 'You' : entry.user.name || `@${entry.user.handle}`}
            </span>

            {/* Value */}
            <span className="text-[11px] font-bold text-slate-300 tabular-nums mt-0.5">
              {formatValue(val, category)}
              <span className="text-[9px] font-medium text-slate-500 ml-0.5">{unitLabel(category)}</span>
            </span>

            {/* Podium block */}
            <div className={`w-full mt-2 rounded-t-xl border ${blockHeights[i]} ${blockBgs[i]} flex items-center justify-center`}>
              <span className="text-lg font-extrabold text-slate-500/25">{rank}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── List row (rank 4+) ─────────────────────────────────

function LeaderboardRow({
  entry,
  rank,
  maxVal,
  category,
  isMe,
}: {
  entry: LeaderboardEntry;
  rank: number;
  maxVal: number;
  category: SortCategory;
  isMe: boolean;
}) {
  const val = sortValue(entry, category);
  const barWidth = maxVal > 0 ? (val / maxVal) * 100 : 0;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
        isMe
          ? 'bg-accent/10 border-accent/30'
          : 'bg-slate-800/60 border-slate-700/30'
      }`}
    >
      {/* Rank */}
      <span className={`w-6 text-center text-sm font-bold shrink-0 ${isMe ? 'text-accent' : 'text-slate-500'}`}>
        {rank}
      </span>

      {/* Avatar */}
      <Avatar user={entry.user} size="sm" />

      {/* Name + bar */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-semibold truncate ${isMe ? 'text-accent' : 'text-slate-200'}`}>
            {isMe ? 'You' : entry.user.name || `@${entry.user.handle}`}
          </span>
          {isMe && (
            <span className="text-[9px] font-semibold text-accent/70 bg-accent/10 px-1.5 py-px rounded">you</span>
          )}
        </div>
        <div className="mt-1 h-[5px] bg-slate-700/40 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              isMe ? 'bg-accent' : 'bg-slate-500/60'
            }`}
            style={{ width: `${Math.max(barWidth, 3)}%` }}
          />
        </div>
      </div>

      {/* Value */}
      <div className="text-right shrink-0">
        <span className={`text-sm font-bold tabular-nums ${isMe ? 'text-accent' : 'text-slate-200'}`}>
          {formatValue(val, category)}
        </span>
        <span className="text-[9px] text-slate-500 ml-0.5">{unitLabel(category)}</span>
      </div>
    </div>
  );
}

// ── "Your Rank" sticky card ────────────────────────────

function YourRankCard({
  entry,
  rank,
  aheadEntry,
  category,
}: {
  entry: LeaderboardEntry;
  rank: number;
  aheadEntry: LeaderboardEntry | null;
  category: SortCategory;
}) {
  const val = sortValue(entry, category);
  const gap = aheadEntry ? sortValue(aheadEntry, category) - val : 0;

  return (
    <div className="sticky bottom-0 -mx-3 mt-4 px-4 py-3 bg-slate-800/95 backdrop-blur-sm border-t border-slate-700/50 flex items-center gap-3">
      <span className="text-lg font-extrabold text-accent tabular-nums">#{rank}</span>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-accent">You</span>
        {aheadEntry && gap > 0 && (
          <p className="text-[11px] text-slate-500 mt-0.5">
            <span className="font-semibold text-slate-400">{formatValue(gap, category)} {unitLabel(category)}</span>
            {' '}behind {aheadEntry.user.name || `@${aheadEntry.user.handle}`}
          </p>
        )}
      </div>
      <div className="text-right">
        <span className="text-base font-bold text-accent tabular-nums">{formatValue(val, category)}</span>
        <span className="text-[10px] text-slate-500 ml-0.5">{unitLabel(category)}</span>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────

export function LeaderboardView() {
  const { currentUser, friends } = useApp();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [category, setCategory] = useState<SortCategory>('miles');

  const visibleFriends = useMemo(
    () => friends.filter((f) => f.leaderboardVisible !== false),
    [friends]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const allIds = [currentUser.id, ...visibleFriends.map((f) => f.id)];
        const allFlights = await getAllFlightsForUsers(allIds);

        const flightsByUser = new Map<string, Flight[]>();
        for (const f of allFlights) {
          const list = flightsByUser.get(f.userId) ?? [];
          list.push(f);
          flightsByUser.set(f.userId, list);
        }

        const allUsers = [currentUser, ...visibleFriends];
        const result: LeaderboardEntry[] = allUsers.map((user) => ({
          user,
          ...computeStats(flightsByUser.get(user.id) ?? []),
        }));

        if (!cancelled) setEntries(result);
      } catch (err) {
        console.warn('[LeaderboardView] Failed to load:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser, visibleFriends]);

  // Sort by selected category
  const sorted = useMemo(
    () => [...entries].sort((a, b) => sortValue(b, category) - sortValue(a, category)),
    [entries, category]
  );

  const myRank = sorted.findIndex((e) => e.user.id === currentUser.id) + 1;
  const myEntry = sorted.find((e) => e.user.id === currentUser.id);
  const aheadEntry = myRank > 1 ? sorted[myRank - 2] : null;
  const topVal = sorted.length > 0 ? sortValue(sorted[0], category) : 1;
  const showStickyCard = myRank > 3;

  // Top 3 for podium, rest for list
  const podiumEntries = sorted.slice(0, Math.min(3, sorted.length));
  const listEntries = sorted.slice(Math.min(3, sorted.length));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 size={24} className="animate-spin mb-2" />
        <p className="text-sm">Loading leaderboard...</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Trophy size={32} className="mb-3 text-slate-600" />
        <p className="text-sm font-medium text-slate-400">No leaderboard data yet</p>
        <p className="text-xs text-slate-600 mt-1">Add flights to start tracking miles</p>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 pb-4">
      {/* Category pills */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-hide">
        {CATEGORIES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { haptic(); setCategory(key); }}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap border transition-colors ${
              category === key
                ? 'bg-accent text-white border-accent dark:text-slate-950'
                : 'bg-transparent text-slate-500 border-slate-700/50 hover:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Podium */}
      <Podium entries={podiumEntries} category={category} currentUserId={currentUser.id} />

      {/* Divider */}
      {listEntries.length > 0 && <div className="h-px bg-slate-700/40 mb-3" />}

      {/* List */}
      <div className="flex flex-col gap-1.5">
        {listEntries.map((entry, idx) => {
          const rank = idx + podiumEntries.length + 1;
          return (
            <LeaderboardRow
              key={entry.user.id}
              entry={entry}
              rank={rank}
              maxVal={topVal}
              category={category}
              isMe={entry.user.id === currentUser.id}
            />
          );
        })}
      </div>

      {/* Footer */}
      <p className="text-center text-[11px] text-slate-600 mt-4">
        Based on all past flights. Distances are great-circle estimates.
      </p>

      {/* Sticky "your rank" card when not in top 3 */}
      {showStickyCard && myEntry && (
        <YourRankCard entry={myEntry} rank={myRank} aheadEntry={aheadEntry} category={category} />
      )}
    </div>
  );
}
